import { useCombobox } from 'downshift';
import memoize from 'lodash/memoize';
import throttle from 'lodash/throttle';
import React, { FunctionComponent, useContext, useState, useTransition, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { RegistrationContext } from '../../Registration.context';
import { AddressData, RegistrationStepConfig } from '../../types';
import { useSnackbar } from '../../../../hooks/snackbar';
import { FormControlSpacing } from '../../../shared/FormControlSpacing';
import { Button } from '../../../shared/Button';
import { Heading } from '../../../shared/Heading';
import { Icon } from '../../../shared/Icon';
import { AutocompleteInput } from '../../../shared/AutocompleteInput';
import { LoqateFindResult, loqateCountryCode, retrieveAddress, searchAddress } from '../../../../utils/loqate';
import { extractAddressFromLabel } from '../../../../utils/loqate/extract-address-from-label';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import { restrictedPostCode, useUserValidationConstraints } from '../../const';
import './AddressSearch.scss';

type AddressSearchProps = {
  /**
   * Called when user requests to switch to manual address entry form
   */
  onEnterManual: (addressSelected: boolean) => void;
  /**
   * Called after the menu open state has changed
   */
  onMenuOpenChange?: (isMenuOpen: boolean) => void;
  /**
   * Starting state for address data.
   */
  initialAddressData: AddressData;
  addressConfig?: RegistrationStepConfig;
};

/**
 * Address search & selection component/
 */
export const AddressSearch: FunctionComponent<AddressSearchProps> = ({
  initialAddressData,
  addressConfig,
  onMenuOpenChange,
  onEnterManual,
}) => {
  const { addSnack } = useSnackbar();
  const { t } = useTranslations();

  const streetAddr = initialAddressData.addrStreetAddress;

  const [isSearching, startTransition] = useTransition();
  const { setValue, reset, formState } = useFormContext<AddressData>();
  const { addressLinePattern } = useUserValidationConstraints();
  const { onAddressSearchFocus, onAddressSearchBlur } = useContext(RegistrationContext);

  const [addressData, setAddressData] = useState<AddressData | undefined>(streetAddr ? initialAddressData : undefined);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [options, setOptions] = useState<LoqateFindResult[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [showManual, setShowManual] = useState(true);
  const [touched, setTouched] = useState(false);

  const firstFocusAddressSearch = useRef(true);

  const addErrorSnack = throttle(
    () =>
      addSnack({
        message: t('registration.step-three.address.error'),
        type: 'error',
      }),
    5000
  );

  const handleMenuChange = (open: boolean): void => {
    setIsMenuOpen(open);
    onMenuOpenChange && onMenuOpenChange(open);
  };

  // TODO: make language prop dynamic
  const handleAddressLookup = memoize(async (address?: string, containerId?: string) => {
    if (address && address.length > 2) {
      handleMenuChange(true);
      try {
        const searchResponse = await searchAddress({
          text: address,
          language: 'en',
          containerId,
        });

        setOptions(searchResponse.Items);
      } catch (e) {
        addErrorSnack();
      }
    } else {
      setOptions([]);
      handleMenuChange(false);
    }
  });

  const isValidAddressData = (addressData: AddressData): boolean => {
    const { fieldSets } = addressConfig || { fieldSets: [] };
    const addressFields = fieldSets[0]?.fields;
    const resultFields: string[] = [];

    addressFields
      .filter((address) => !address.optional)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      .forEach((address) => resultFields.push(addressData[address.name]));

    return resultFields.every((addressItem) => Boolean(addressItem));
  };

  const handleSelect = async (selectedResult: LoqateFindResult): Promise<void> => {
    if (selectedResult.Type === 'Address') {
      startTransition(() => {
        (async (): Promise<void> => {
          try {
            handleMenuChange(false);
            /**
             * Setting a default empty model incase everything has gone wrong, at least then the user
             * can manually enter their address. We'll then trigger an error with the details to new
             * relic, so we can fix it.
             */
            let addressData = {
              addrStreetAddress: '',
              addrLocality: '',
              addrRegion: '',
              addrPostalCode: '',
              addrCountryCode: loqateCountryCode,
            };

            try {
              addressData = extractAddressFromLabel(selectedResult);

              !addressLinePattern.test(addressData.addrStreetAddress) && onEnterManual(true);

              if (Math.random() <= 0.1) {
                /**
                 * We don't need to do this any more, however this is the only way loqate charge us.
                 * Let's add an occasional call to loqate here, just so they don't suspend our account.
                 */
                retrieveAddress(selectedResult.Id);
              }
            } catch (e) {
              console.error('Search address error: ', e);
            }

            setSearchText(addressData.addrStreetAddress);

            for (const [key, value] of Object.entries(addressData)) {
              setValue(key as keyof AddressData, value, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }

            if (addressData.addrPostalCode?.toUpperCase().startsWith(restrictedPostCode)) {
              setTouched(true);

              return;
            }

            setAddressData(addressData);

            if (!isValidAddressData(addressData)) {
              onEnterManual(true);
            }
          } catch (e) {
            addErrorSnack();
          }
        })();
      });
    } else {
      setOptions([]);
      handleAddressLookup(searchText, selectedResult.Id);
    }
  };

  return (
    <div className="address-search">
      {!addressData && (
        <FormControlSpacing>
          <AutocompleteInput<LoqateFindResult>
            value={searchText}
            onMenuOpenChange={(change): void => {
              if (change.type === useCombobox.stateChangeTypes.InputBlur) {
                handleMenuChange(!!change.isOpen);
              }
            }}
            isMenuOpen={isMenuOpen}
            loadingOptionsPlaceholder={isSearching ? 'Loading...' : null}
            inputProps={{
              label: t('register.step.address.postal-code.label'),
              showFeedbackIcon: false,
              iconRight: showManual && (
                <Button
                  type="button"
                  variant="text"
                  size="small"
                  onClick={(): void => {
                    onEnterManual(false);
                    handleMenuChange(false);
                  }}
                >
                  {t('input.manual-edit')}
                </Button>
              ),
              validationState:
                ((formState.submitCount > 0 && !formState.isValid) || touched) &&
                !isMenuOpen &&
                !isSearching &&
                ((searchText && !addressData) || searchText === '')
                  ? 'error'
                  : undefined,
              validationMessages: [
                {
                  message:
                    (formState.errors.addrPostalCode?.message as string) || t('validation.address-entry.invalid'),
                  id: 'empty-address-text',
                },
              ],
              onBlur: (): void => {
                onAddressSearchBlur?.();
                setTouched(true);
              },
              onFocus: (): void => {
                onAddressSearchFocus?.();
                setShowManual(true);

                if (options.length > 0) {
                  handleMenuChange(true);
                }
              },
            }}
            onInputChange={(s?: string): void => {
              setSearchText(s || '');
              handleAddressLookup(s);
              setShowManual(s !== '');

              if (s?.length === 1 && firstFocusAddressSearch.current) {
                firstFocusAddressSearch.current = false;
              }
            }}
            name="autoComplete"
            options={options}
            optionToString={(result: LoqateFindResult | null): string =>
              result ? `${result.Text}, ${result.Description}` : ''
            }
            onSelect={handleSelect}
          />
        </FormControlSpacing>
      )}

      {addressData && (
        <div className="address-search__selected-address">
          <div className="address-search__address">
            <div>
              <Heading level={6} className="address-search__line">
                {addressData.addrStreetAddress}
              </Heading>
              <span className="address-search__city">{addressData.addrLocality}</span>
              <span className="address-search__county">{addressData.addrRegion}</span>
              <span className="address-search__postCode">{addressData.addrPostalCode}</span>
            </div>
            <Icon variant="PinHome" />
          </div>

          <div className="address-search__selected-address-actions">
            <Button
              type="button"
              variant="text"
              size="small"
              icon={<Icon variant="Edit" />}
              iconPosition="left"
              onClick={(): void => {
                onEnterManual(true);
              }}
            >
              {t('input.search-address.edit')}
            </Button>

            <Button
              type="button"
              variant="text"
              size="small"
              icon={<Icon variant="SearchLocation" />}
              iconPosition="left"
              onClick={(): void => {
                setSearchText('');
                setAddressData(undefined);
                reset(initialAddressData);
                setTouched(false);
              }}
            >
              {t('input.search-address.look-up')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
